// Card payments — shared logic for the checkout and the /placanje page. EUR orders in
// Croatia / the EU pay through Fulfillio (Stripe), Serbian RSD orders through Monri.
//
// Checkout places the order with paymentMethod = CARD_PAYMENT, then sends the buyer to
// /placanje/:orderId. That page asks the backend for a payment, shows the provider's card
// form (`checkout.provider`), and after paying polls the backend until the provider's
// webhook marks the order paid. The browser never decides an order is paid — only the
// webhook does.

import React from 'react';
import { loadStripe } from '@stripe/stripe-js';

export const CARD_PAYMENT = 'Plaćanje karticom';

// Set to false to turn off EUR card payment: checkout then offers only pouzeće and hides
// the card-only EU countries.
export const CARD_PAYMENTS_ENABLED = true;

// Keep in sync with card_countries in backend/config/fulfillio.php
export const CARD_COUNTRIES = [
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU',
  'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK',
];

// Monri (Serbia, RSD) — keep in sync with card_countries in backend/config/monri.php
export const MONRI_COUNTRIES = ['RS'];

export const isCardPaymentAvailable = (country, currency) =>
  (CARD_PAYMENTS_ENABLED && currency === 'EUR' && CARD_COUNTRIES.includes(country))
  || (currency === 'RSD' && MONRI_COUNTRIES.includes(country));

// Stripe Elements locale for the storefront's display language
export const stripeLocale = (language) => ({ BA: 'hr', RU: 'ru' }[language] || 'en');

const stripePromises = {};
export const getStripe = (publishableKey) => {
  if (!stripePromises[publishableKey]) stripePromises[publishableKey] = loadStripe(publishableKey);
  return stripePromises[publishableKey];
};

// Monri card component locale for the storefront's display language
export const monriLocale = (language) => (language === 'BA' ? 'sr' : 'en');

// Monri's components.js has to load straight from Monri (PCI) — never bundle it.
let monriPromise = null;
export const loadMonri = (scriptUrl) => {
  if (window.Monri) return Promise.resolve(window.Monri);
  if (!monriPromise) {
    monriPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      script.onload = () => (window.Monri ? resolve(window.Monri) : reject(new Error('Monri not loaded')));
      script.onerror = () => {
        monriPromise = null;
        script.remove();
        reject(new Error('Monri not loaded'));
      };
      document.head.appendChild(script);
    });
  }
  return monriPromise;
};

export const paymentReturnUrl = (orderId) =>
  `${window.location.origin}/placanje/${encodeURIComponent(orderId)}`;

const POLL_INTERVAL_MS = 2000;
const POLL_ATTEMPTS = 15;
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * State of the card payment for one order.
 *
 * phase: loading | form (show Payment Element) | checking (waiting for the webhook) |
 *        processing (still not final after polling) | paid | canceled | error
 * `error` is an English message to pass through translate().
 */
export function useCardPayment(apiUrl, orderId) {
  const [phase, setPhase] = React.useState('loading');
  const [checkout, setCheckout] = React.useState(null);
  const [order, setOrder] = React.useState(null);
  const [error, setError] = React.useState(null);
  const alive = React.useRef(true);

  React.useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const base = `${apiUrl}/api/payments/${encodeURIComponent(orderId)}`;

  // Wait for the webhook after Stripe says the card went through. Only "paid" and
  // "canceled" end it: a "failed" here would be an earlier attempt's webhook.
  const waitForWebhook = React.useCallback(async () => {
    setPhase('checking');
    for (let i = 0; i < POLL_ATTEMPTS; i++) {
      try {
        const res = await fetch(base, { headers: { Accept: 'application/json' } });
        const data = await res.json();
        if (!alive.current) return;
        if (data.status === 'paid') {
          setOrder(data.order);
          setPhase('paid');
          return;
        }
        if (data.status === 'canceled') {
          setPhase('canceled');
          return;
        }
      } catch (_) {
        // network blip — keep polling
      }
      await sleep(POLL_INTERVAL_MS);
      if (!alive.current) return;
    }
    setPhase('processing');
  }, [base]);

  const loadCheckout = React.useCallback(async () => {
    setPhase('loading');
    try {
      const res = await fetch(`${base}/intent`, { method: 'POST', headers: { Accept: 'application/json' } });
      const data = await res.json();
      if (!alive.current) return;
      if (!res.ok) throw new Error(data.message);

      setCheckout(data);
      if (data.clientSecret) {
        setPhase('form');
      } else if (data.status === 'paid') {
        setOrder(data.order);
        setPhase('paid');
      } else if (data.status === 'canceled' || data.payment_state === 'canceled') {
        setPhase('canceled');
      } else {
        waitForWebhook();
      }
    } catch (_) {
      if (!alive.current) return;
      setError('Card payment is unavailable right now. Please try again later or contact us on Instagram.');
      setPhase('error');
    }
  }, [base, waitForWebhook]);

  // Back from a Stripe redirect (3-D Secure etc.)? Read once, then drop it from the URL.
  const [redirectStatus] = React.useState(() => new URLSearchParams(window.location.search).get('redirect_status'));

  React.useEffect(() => {
    if (redirectStatus) {
      window.history.replaceState(null, '', window.location.pathname);
    }

    if (redirectStatus === 'succeeded' || redirectStatus === 'processing') {
      waitForWebhook();
    } else {
      if (redirectStatus === 'failed') setError('Payment failed. Please try again.');
      loadCheckout();
    }
  }, [redirectStatus, loadCheckout, waitForWebhook]);

  return { phase, checkout, order, error, setError, waitForWebhook, reload: loadCheckout };
}

/** Meta Pixel purchase for a paid card order — once per order, even across reloads. */
export function orderPixelPurchase(order, pixelPurchase) {
  const key = `pixel-purchase-${order.orderId}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch (_) {
    // storage blocked — fire anyway
  }

  let items = order.orderedItemsDetails;
  if (typeof items === 'string') {
    try { items = JSON.parse(items); } catch (_) { items = []; }
  }
  const products = (items || []).map(i => ({ id: i.id, qty: i.qty, item_price: parseFloat(i.price) || 0 }));
  const total = (parseFloat(order.total || 0) + parseFloat(order.shippingCost || 0)).toFixed(2);
  pixelPurchase(products, total, order.currency, order.orderId);
}
